import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { CreditCard, Loader2 } from "lucide-react";
import { paymentsApi } from "../../lib/api";
import { toast } from "sonner";
import { captureError } from "../../lib/sentry";

const PaymentMethodsSection = () => {
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchMethods = async () => {
    try {
      const res = await paymentsApi.getMethods();
      setMethods(res.data.methods || []);
    } catch (error) {
      captureError(error, { action: "load_payment_methods" });
      toast.error("Failed to load saved payment details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMethods();
  }, []);

  const handleRemove = async (id) => {
    setActionLoading(id);
    try {
      await paymentsApi.removeMethod(id);
      setMethods((prev) => prev.filter((m) => m.id !== id));
      toast.success("Card removed");
    } catch (error) {
      captureError(error, { action: "remove_payment_method", methodId: id });
      toast.error("Failed to remove card");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">Payment</CardTitle>
        </div>
        <CardDescription>Manage your saved payment details</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : methods.length > 0 ? (
          <div className="space-y-3" data-testid="payment-methods-list">
            {methods.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-border"
                data-testid={`payment-method-${m.last4}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium capitalize">
                    {m.brand} ending in {m.last4}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Expires {String(m.exp_month).padStart(2, "0")}/{m.exp_year}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-8 text-destructive hover:text-destructive"
                  onClick={() => handleRemove(m.id)}
                  disabled={actionLoading === m.id}
                  data-testid={`remove-card-${m.last4}`}
                >
                  {actionLoading === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Remove"}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No saved payment details yet.</p>
        )}

        <Button
          variant="outline"
          className="w-full h-11 rounded-xl"
          onClick={() => navigate("/add-payment")}
          data-testid="add-payment-details-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" /> Add Payment Details
        </Button>
      </CardContent>
    </Card>
  );
};

export default PaymentMethodsSection;
